import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  type CategoriaTorneio,
  type InscricoesPorTorneioData,
} from '../../../services/torneioService';
import InscricaoStatusChip from '../../../components/admin/InscricaoStatusChip';
import { INSCRICAO_STATUS_FILTER_OPTIONS } from '../../../utils/inscricaoStatusDisplay';

const COLUMN_COUNT = 5;

const STATUS_OPTIONS: { label: string; value: string | null }[] = [
  { label: 'Todos', value: null },
  ...INSCRICAO_STATUS_FILTER_OPTIONS,
];

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

interface TournamentFinanceiroInscricoesTabProps {
  torneioId: string;
  categorias: CategoriaTorneio[];
  inscricoes: InscricoesPorTorneioData['inscricoesTorneioPorAtletaIdTorneioId'];
}

const TournamentFinanceiroInscricoesTab: React.FC<TournamentFinanceiroInscricoesTabProps> = ({
  torneioId,
  categorias,
  inscricoes,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoriaFilter, setCategoriaFilter] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string | null>(null);
  const [dataFim, setDataFim] = useState<string | null>(null);

  const categoriaOptions = useMemo(
    () =>
      categorias
        .map((cat) => ({ id: cat.id, nome: cat.nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome)),
    [categorias],
  );

  const hasDateError = !!(
    dataInicio &&
    dataFim &&
    dayjs(dataInicio).isAfter(dayjs(dataFim))
  );

  const filteredRows = useMemo(() => {
    let rows = [...inscricoes];

    if (categoriaFilter) {
      rows = rows.filter((r) => r.categoriaTorneio?.id === categoriaFilter);
    }

    if (statusFilter) {
      rows = rows.filter((r) => r.status === statusFilter);
    }

    if (dataInicio && !hasDateError) {
      const start = dayjs(dataInicio).startOf('day');
      rows = rows.filter((r) =>
        r.dataInscricao
          ? dayjs(r.dataInscricao).isAfter(start) || dayjs(r.dataInscricao).isSame(start)
          : false,
      );
    }

    if (dataFim && !hasDateError) {
      const end = dayjs(dataFim).endOf('day');
      rows = rows.filter((r) =>
        r.dataInscricao
          ? dayjs(r.dataInscricao).isBefore(end) || dayjs(r.dataInscricao).isSame(end)
          : false,
      );
    }

    return rows;
  }, [inscricoes, categoriaFilter, statusFilter, dataInicio, dataFim, hasDateError]);

  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatusFilter(null);
    setCategoriaFilter(null);
    setDataInicio(null);
    setDataFim(null);
    setPage(0);
  };

  const linkStyle = { color: '#1976d2', textDecoration: 'none' as const };

  const renderAtletaLinks = (
    atletas?: { id: string; usuario: { id: string; nome: string } }[],
  ) => {
    if (!atletas || atletas.length === 0) return '-';
    return atletas.map((a, i) => (
      <React.Fragment key={a.id}>
        {i > 0 && ', '}
        <Link to={`/userpage/${a.id}`} style={linkStyle}>
          {a.usuario?.nome ?? 'Atleta'}
        </Link>
      </React.Fragment>
    ));
  };

  return (
    <>
      {/* Filter Bar */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Categoria"
              size="small"
              fullWidth
              value={categoriaFilter || ''}
              onChange={(e) => {
                setCategoriaFilter(e.target.value || null);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todas</MenuItem>
              {categoriaOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nome}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={statusFilter || ''}
              onChange={(e) => {
                setStatusFilter(e.target.value || null);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Início"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataInicio || ''}
              onChange={(e) => {
                setDataInicio(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataFim || ''}
              onChange={(e) => {
                setDataFim(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
            />
          </Box>
          <Box sx={{ flex: '0 1 auto' }}>
            <Button variant="outlined" color="inherit" size="small" onClick={handleResetFilters}>
              Limpar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Equipe</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Atletas</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Data Inscrição</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  Nenhuma inscrição encontrada.
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    {r.categoriaTorneio?.id ? (
                      <Link
                        to={`/tournament/${torneioId}/category/${r.categoriaTorneio.id}`}
                        style={linkStyle}
                      >
                        {r.categoriaTorneio.nome}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{r.equipe?.nome || '-'}</TableCell>
                  <TableCell>{renderAtletaLinks(r.equipe?.atletas)}</TableCell>
                  <TableCell>
                    <InscricaoStatusChip
                      status={r.status || ''}
                      pagamentoStatus={r.pagamentoInscricao?.status}
                      isento={r.isento}
                    />
                  </TableCell>
                  <TableCell>{formatDate(r.dataInscricao)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </TableContainer>
    </>
  );
};

export default TournamentFinanceiroInscricoesTab;
